import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateUserPreferences } from "../api/mutations";
import { useGetUserPreferences } from "../api/queries";
import { useNotification } from "../hooks/useNotification";
import Modal from "../componentLibrary/Modal";
import Button from "../componentLibrary/Button";
import Checkbox from "../componentLibrary/Checkbox";

interface SaveAnswersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (saveAnswers: boolean) => void;
}

export default function SaveAnswersModal({
  isOpen,
  onClose,
  onConfirm,
}: SaveAnswersModalProps) {
  const queryClient = useQueryClient();
  const { data: userPreferences } = useGetUserPreferences();
  const { notify } = useNotification();

  const updatePreferencesMutation = useUpdateUserPreferences({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
      notify("success", "Preferences updated successfully");
    },
    onError: async () => {
      notify("error", "Failed to save preference");
    },
  });

  const handleSaveAnswers = async (saveToToolbox: boolean) => {
    // If askEveryTime is false, save the user's choice as their default preference
    if (userPreferences && !userPreferences.askEveryTime) {
      await updatePreferencesMutation.mutateAsync({
        saveAnswersToLog: saveToToolbox,
        askEveryTime: false, // Keep askEveryTime as false
      });
    }

    // Proceed with the user's choice
    onConfirm(saveToToolbox);
    onClose();
  };

  const handleAskEveryTimeChange = (checked: boolean) => {
    // If userPreferences don't exist yet, create them with the new askEveryTime value
    if (!userPreferences) {
      updatePreferencesMutation.mutate({
        saveAnswersToLog: false, // Default value for new users
        askEveryTime: checked,
      });
      return;
    }

    // Only save if the askEveryTime preference actually changed
    if (checked !== userPreferences.askEveryTime) {
      updatePreferencesMutation.mutate({
        saveAnswersToLog: userPreferences.saveAnswersToLog ?? false,
        askEveryTime: checked,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCloseModal={onClose}
      title="Save Responses to Toolbox?"
      size="lg"
      height="h-auto"
      backgroundColor="bg-white"
    >
      <div className="space-y-4 m-2">
        <div className="space-y-3">
          <div className="flex flex-row space-x-3">
            <Button
              variant="primary"
              onClick={() => handleSaveAnswers(true)}
              className="flex-1 h-16"
              size="md"
            >
              Yes, Save to
              <br />
              My Toolbox
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleSaveAnswers(false)}
              className="flex-1 h-16"
              size="md"
            >
              No, Delete
              <br />
              My Responses
            </Button>
          </div>

          <div className="flex justify-center items-center">
            <Checkbox
              checked={userPreferences?.askEveryTime ?? true}
              onChange={(e) => handleAskEveryTimeChange(e.target.checked)}
              label=""
            />
            <span className="text-gray-700">Ask me every time</span>
          </div>
        </div>

        <div className="text-sm text-gray-500 text-center">
          You can change this preference anytime in your profile settings.
        </div>
      </div>
    </Modal>
  );
}
